import { Container, Graphics } from 'pixi.js-legacy';
import type { Player } from '../../entities/Player';
import { hexStringToNumber } from '../../utils/color';

/**
 * Player PixiJS 어댑터
 * Player 엔티티를 PixiJS Graphics로 렌더링
 */
export class PlayerAdapter {
    private container: Container;
    private playerGraphics: Graphics | null = null;
    private shieldGraphics: Graphics | null = null;
    private lastStage: number = -1;
    private lastShieldRadius: number = -1;  // 보호막 dirty flag용

    constructor(container: Container) {
        this.container = container;
    }

    /**
     * Player 동기화 및 렌더링
     */
    sync(player: Player, color: string = '#FFFFFF', opacity: number = 1.0, lineWidth: number = 1): void {
        // 플레이어 Graphics 생성 또는 업데이트
        if (!this.playerGraphics) {
            this.playerGraphics = new Graphics();
            this.container.addChild(this.playerGraphics);
        }

        // 보호막 Graphics 생성 또는 업데이트
        if (!this.shieldGraphics) {
            this.shieldGraphics = new Graphics();
            this.container.addChild(this.shieldGraphics);
        }

        // stage가 변경되었거나 처음인 경우 다각형 다시 그리기
        if (this.lastStage !== player.stage) {
            this.lastStage = player.stage;
            this.redrawPolygon(player);
        }

        // 위치 및 회전 업데이트
        this.playerGraphics.position.set(player.x, player.y);
        this.playerGraphics.rotation = player.rotation;
        this.playerGraphics.alpha = opacity;

        // 색상 업데이트
        const colorNum = hexStringToNumber(color);
        this.playerGraphics.tint = colorNum;

        // 보호막 업데이트
        this.updateShield(player, color, opacity, lineWidth);
    }

    /**
     * 다각형 다시 그리기
     */
    private redrawPolygon(player: Player): void {
        if (!this.playerGraphics) return;

        this.playerGraphics.clear();

        const sides = player.stage + 2; // stage 1=삼각형(3), 2=사각형(4), etc.
        const size = player.radius;

        // 정다각형 그리기
        const points: number[] = [];
        for (let i = 0; i < sides; i++) {
            const angle = (i * 2 * Math.PI / sides) - Math.PI / 2;
            points.push(Math.cos(angle) * size, Math.sin(angle) * size);
        }

        this.playerGraphics.lineStyle(1, 0xFFFFFF);
        this.playerGraphics.drawPolygon(points);
    }

    /**
     * 보호막 업데이트 (dirty flag 패턴 - clear() 최소화)
     */
    private updateShield(player: Player, color: string, opacity: number, lineWidth: number): void {
        if (!this.shieldGraphics) return;

        if (player.hasShield) {
            const shieldRadius = player.radius * 1.5;

            // radius가 변경된 경우에만 다시 그림 (clear() 호출 최소화)
            if (this.lastShieldRadius !== shieldRadius) {
                this.lastShieldRadius = shieldRadius;
                this.shieldGraphics.clear();
                this.shieldGraphics.lineStyle(lineWidth, 0xFFFFFF, 0.5);
                this.shieldGraphics.drawCircle(0, 0, shieldRadius);
            }

            // 위치와 색상만 업데이트 (clear 없이)
            this.shieldGraphics.visible = true;
            this.shieldGraphics.position.set(player.x, player.y);
            this.shieldGraphics.tint = hexStringToNumber(color);
            this.shieldGraphics.alpha = opacity;
        } else {
            this.shieldGraphics.visible = false;
        }
    }

    /**
     * 플레이어 숨기기 (게임오버 시)
     */
    hide(): void {
        if (this.playerGraphics) {
            this.playerGraphics.visible = false;
        }
        if (this.shieldGraphics) {
            this.shieldGraphics.visible = false;
        }
    }

    /**
     * 플레이어 보이기
     */
    show(): void {
        if (this.playerGraphics) {
            this.playerGraphics.visible = true;
        }
    }

    /**
     * 참조 초기화 (clearAll 후 다시 생성할 수 있도록)
     */
    reset(): void {
        this.playerGraphics = null;
        this.shieldGraphics = null;
        this.lastStage = -1;
    }

    /**
     * 정리
     */
    clear(): void {
        if (this.playerGraphics) {
            this.playerGraphics.clear();
            this.playerGraphics.visible = true;  // reset visibility
        }
        if (this.shieldGraphics) {
            this.shieldGraphics.clear();
            this.shieldGraphics.visible = false;
        }
        this.lastStage = -1;
        this.lastShieldRadius = -1;
    }

    /**
     * 완전히 파괴
     */
    destroy(): void {
        if (this.playerGraphics) {
            this.playerGraphics.destroy();
            this.playerGraphics = null;
        }
        if (this.shieldGraphics) {
            this.shieldGraphics.destroy();
            this.shieldGraphics = null;
        }
    }
}
